import {
  BasePayloadMapper,
  DateFormatter,
  Nullable,
  omitNullValues,
} from '@ps/utils';
import { SURVEY_STATUS } from '@ps/survey-kernel';
import {
  AdvancedFiltersLookupScope,
  FeasibilityAdvancedFilterCriteriaType,
  FeasibilityFilterCriteria,
  FindSurveysParams,
  SurveySearchParams,
  SurveySearchType,
} from '@ps/survey-utils';

import { SurveyLookupParamsDto } from '../dtos/survey-lookup.dto';

const LOOKUP_STATUS = [SURVEY_STATUS.CLOSED, SURVEY_STATUS.INVOICED];
const INCLUSION_FLAG = { include: '1', exclude: '0' } as const;

// Which field the endpoint matches a typed keyword against — `tle` is the survey title.
const SEARCH_KEY: Record<SurveySearchType, string> = {
  billingNumber: 'survey_po',
  surveyTitle: 'tle',
  group: 'id',
};

/**
 * Maps a `FindSurveysParams` and `SurveySearchParams` to a `SurveyLookupParamsDto` for the `survey-lookup` endpoint.
 * It is used by both the `getSurveySummaries` and `searchSurveySummaries` endpoints.
 */
export class SurveyLookupPayloadMapper extends BasePayloadMapper<
  FindSurveysParams,
  SurveyLookupParamsDto
> {
  /**
   * The committed Advanced Filters lookup — criteria and date range, no keyword.
   */
  public toPayload(params: FindSurveysParams): SurveyLookupParamsDto {
    const { criteria } = params;

    return {
      ...this._scopeToPayload(params),
      ...omitNullValues({
        projectManagerIds: this._idsFor(criteria, 'projectManager'),
        billingNumbers: this._idsFor(criteria, 'billingNumber'),
        templateIds: this._idsFor(criteria, 'template'),
        surveyIds: this._idsFor(criteria, 'surveyTitle'),
      }),
    };
  }

  /**
   * The free-text search lookup — keyword and date range, no criteria.
   * The date range is omitted if the search type is `group`.
   */
  public toSearchSurveyPayload(
    params: SurveySearchParams,
  ): SurveyLookupParamsDto {
    return {
      ...this._scopeToPayload(params),
      ...(params.searchType === 'group' ? { fromDate: '', toDate: '' } : {}),
      searchKey: SEARCH_KEY[params.searchType],
      searchValue: params.value,
    };
  }

  /** What both requests send whatever they are looking for. */
  private _scopeToPayload({
    dateRange,
    countryCode,
    languageCode,
    filterType,
  }: AdvancedFiltersLookupScope): SurveyLookupParamsDto {
    return {
      fromDate: DateFormatter.for(dateRange?.start).format(),
      toDate: DateFormatter.for(dateRange?.end).format(),
      status: LOOKUP_STATUS.join(','),
      countryCode,
      languageCode,
      incl_excl: INCLUSION_FLAG[filterType],
    };
  }

  private _idsFor(
    criteria: readonly FeasibilityFilterCriteria[],
    type: FeasibilityAdvancedFilterCriteriaType,
  ): Nullable<string> {
    const values =
      criteria.find((criterion) => criterion.type === type)?.values ?? [];

    return values.length ? values.map((value) => value.id).join(',') : null;
  }
}
